import Link from 'next/link'
import styled, { css } from 'styled-components'

import Image from 'shared/components/Image'
import { useHasCmsBanner } from 'shared/hooks/useHasCmsBanner'
import { useWindowSize } from 'shared/hooks/useWindowSize'
import { colors, fonts, media } from 'shared/lib'
import { ArticleBanner } from 'shared/types/ArticleBanner'

type EditorialHeroProps = {
  banners: Array<ArticleBanner>
}

export const EditorialHero = ({ banners }: EditorialHeroProps) => {
  const { isLoading, isMobile } = useWindowSize()
  const { hasCmsBanner } = useHasCmsBanner()
  if (!banners || banners?.length === 0) {
    return null
  }

  const desktopBanner = banners[0]
  const mobileBanner = banners?.length > 1 ? banners[1] : desktopBanner
  const banner = isMobile ? mobileBanner : desktopBanner

  const ContainerBody = (
    <>
      <StyledImage
        $isLoadingWindow={isLoading}
        alt={banner?.mediaAsset?.seoDescription}
        data-qa="EditorialHeroImage"
        fill
        sizes="100vw"
        src={banner?.mediaAsset?.largeUrl}
        priority={true}
      />
      <TitleContainer>
        <TypeCopy data-qa="EditorialHeroTitle">{banner?.title}</TypeCopy>
        <TitleCopy data-qa="EditorialHeroSubtitle">{banner?.subtitle}</TitleCopy>
      </TitleContainer>
      {banner?.buttonLabel && (
        <FeaturedCopy data-qa="EditorialHeroFeatured">{banner?.buttonLabel}</FeaturedCopy>
      )}
    </>
  )

  return banner?.buttonLink ? (
    <ContainerLink
      $hasCmsBanner={hasCmsBanner}
      className="transparent-nav-overlay"
      data-qa="EditorialHeroLink"
      href={banner?.buttonLink}
    >
      {ContainerBody}
    </ContainerLink>
  ) : (
    <ContainerDiv
      $hasCmsBanner={hasCmsBanner}
      className="transparent-nav-overlay"
      data-qa="EditorialHeroLink"
    >
      {ContainerBody}
    </ContainerDiv>
  )
}

const NAV_HEIGHT = 72
const ContainerStyles = css<{ $hasCmsBanner: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 120px;
  width: 100vw;

  ${media.medium`
    margin-bottom: 120px;
  `};

  ${({ $hasCmsBanner }) =>
    // Container height is calculated based on the hero image height
    // and whether the CMS banner is present.
    // Mobile: 100vh - NAV_HEIGHT - 54px (cms banner)
    // Desktop: 100vh - NAV_HEIGHT - 39px (cms banner)

    $hasCmsBanner
      ? css`
          height: calc(100vh - ${NAV_HEIGHT}px - 54px);
          ${media.medium`
            height: calc(100vh - ${NAV_HEIGHT}px - 39px);
          `};
        `
      : css`
          height: calc(100vh - ${NAV_HEIGHT}px);
          ${media.medium`
            height: calc(100vh - ${NAV_HEIGHT}px);
          `};
        `};
`
const ContainerDiv = styled.div<{ $hasCmsBanner: boolean }>`
  ${ContainerStyles}
`
const ContainerLink = styled(Link)<{ $hasCmsBanner: boolean }>`
  ${ContainerStyles}
`
const FeaturedCopy = styled.div`
  ${fonts.STANDARD};
  align-items: center;
  bottom: 40px;
  color: ${colors.FC2_WHITE};
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 400;
  justify-content: center;
  line-height: 120%;
  position: absolute;
  text-align: center;
  text-wrap: balance;
  text-transform: uppercase;
  width: 100vw;
`
const StyledImage = styled(Image)<{ $isLoadingWindow: boolean }>`
  display: block;
  object-fit: cover !important;
  width: 100vw !important;

  ${media.medium`
    height: 100vh !important;
    width: 100% !important;
  `};

  ${({ $isLoadingWindow }) =>
    $isLoadingWindow &&
    css`
      display: none;
    `};
`
const TitleContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100vh;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100vw;
`
const TitleCopy = styled.div`
  color: ${colors.FC2_WHITE};
  font-family: 'Ivar Headline'; /* stylelint-disable-line font-family-no-missing-generic-family-keyword */
  font-size: 24px;
  font-weight: 400;
  letter-spacing: -0.24px;
  text-align: center;
  text-wrap: balance;
  text-transform: uppercase;

  ${media.medium`
    font-size: 32px;
    letter-spacing: -0.32px;
  `};
`
const TypeCopy = styled.div`
  color: ${colors.FC2_WHITE};
  ${fonts.STANDARD};
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  margin-bottom: 4px;
  text-align: center;
  text-wrap: balance;
  text-transform: uppercase;
`
